import type { AuthFetch } from "@p0security/frontend-core";
import { Tenant } from "@p0security/frontend-core";
import * as firestoreProvider from "@p0security/frontend-core/react";
import type { OktaIntegration } from "@p0security/integrations-okta-shared/types";
import { anyIsInstalled } from "@p0security/shared/install/installed";
import type { DirectoryGroup } from "@p0security/shared/types/directory";
import { useCallback, useContext, useEffect, useState } from "react";

/***
 * useOktaGroups Hook
 * @param authFetch - A function that will make an authenticated request to the api backend
 * @returns {Object} - {
 * oktaData: OktaIntegration,
 * loading: boolean,
 * refresh: () => Promise<string[]>,
 * }
 * @description - This hook will return the okta integration data, a boolean indicating if the data is loading, and a function that will refresh the okta groups
 * It will return the following:
 * groups - DirectoryGroup[] - the groups object from the backend
 * loading - boolean - true if the hook is currently fetching data from the firebase
 * refresh - () => Promise<string[]> - a function to refresh the Okta groups
 */
export const useOktaGroups = (authFetch: AuthFetch) => {
  const [groups, setGroups] = useState<DirectoryGroup[]>();
  const tenantId = useContext(Tenant);
  const integrationData = firestoreProvider.useFirestoreDoc<OktaIntegration>(
    `o/${tenantId}/integrations/okta`,
    { live: true }
  );
  const refresh = useCallback(async () => {
    if (!integrationData.doc || !anyIsInstalled(integrationData.doc?.data)) {
      return;
    }
    const response = await authFetch(`integrations/okta/refresh`, {
      method: "POST",
    });
    if (!response) return;
    const { groups } = await response.json();
    setGroups(groups);
  }, [authFetch, integrationData.doc]);

  useEffect(() => {
    refresh().catch(console.error);
  }, [integrationData.doc?.data, refresh]);

  return { groups, loading: integrationData.loading, refresh };
};
